import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { MyButton, MyInput, MyPagination } from "../../../components/Commons";
import { useTran } from "../../../hooks/useTran";
import { Building2, Search, Upload } from "lucide-react";
import CreateCompanyModal from "../components/CreateCompanyModal";
import DuplicateCompanyModal from "../components/DuplicateCompanyModal";
import { useCompanyStore } from "../hooks/useCompanyStore";
import CompanyCard from "../components/CompanyCard";
import debounce from "lodash/debounce";
import LoadingPage from "../../CNP/pages/LoadingPage";
import { useUserStore } from "../../User/hooks/useUserStore";
import CreateUserModal from "../../User/components/CreateUserModal";
import DeleteConfirmModal from "../../../components/Commons/DeleteConfirmModal";
import BatchUploadModal from "../components/BatchUploadModal";
import { useAuthStore } from "../../Auth";
import {
  COMPANY_MANAGEMENT,
  USER_MANAGEMENT,
} from "../../User/constants/userModules.constants";
import { RESELLER } from "../../../constants/constants";

const CompanyManagementPage = observer(() => {
  const { t } = useTran();

  const [loading, setLoading] = useState(true);

  const { permissions, role } = useAuthStore();
  const permissionList = permissions || [];
  const companyModulePermission =
    permissionList.filter(
      (m) => m.moduleName.value === COMPANY_MANAGEMENT,
    )[0] || {};
  const userModulePermission =
    permissionList.filter((m) => m.moduleName.value === USER_MANAGEMENT)[0] ||
    {};
  const isEditable =
    companyModulePermission.create && companyModulePermission.update;

  const {
    getCompanies,
    totalCompanies,
    companies,
    page,
    limit,
    setPage,
    search,
    setSearch,
    getCompanyList,
    openCompanyModal,
    handleDeleteCompany,
    selectedCompanyName,
    setSelectedCompanyId,
    setIsBatchUploadModalOpen,
    isDeleteConfirmModalOpen,
    closeDeleteConfirmModal,
    getCountryList,
  } = useCompanyStore();

  const { errors, getRoles } = useUserStore();

  const fetchCompanies = useCallback(
    debounce(async () => {
      setLoading(true);
      const success = await getCompanies();
      if (success) setTimeout(() => setLoading(false), 800);
    }, 300),
    [limit],
  );

  const _handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
    fetchCompanies();
  };

  useEffect(() => {
    setSelectedCompanyId();
    fetchCompanies();
    getCompanyList();
    getRoles();
    getCountryList();
  }, [page, search, fetchCompanies]);

  const totalPages = Math.ceil(totalCompanies / limit);

  return (
    <div className="flex flex-col gap-y-3 h-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <img src="/icon.svg" className="w-[19px] me-1" />
          <p className="text-xl font-semibold">{t("Company Management")}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          <p>
            {t("All Companies") + " : "}
            <span className="text-primary">{totalCompanies}</span>
          </p>
          {totalPages !== 1 && (
            <MyPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
        <div className="flex items-center gap-x-2">
          <MyInput
            placeholder={t("Search")}
            icon={Search}
            value={search}
            onChange={_handleSearchChange}
          />
          {isEditable && (
            <>
              {role === RESELLER && (
                <MyButton
                  onClick={() => setIsBatchUploadModalOpen(true)}
                  className="gap-x-2"
                >
                  <Upload size={16} />
                  {t("Batch Upload")}
                </MyButton>
              )}
              <MyButton onClick={() => openCompanyModal()} className="gap-x-2">
                <Building2 size={16} />
                {t("Add Company")}
              </MyButton>
            </>
          )}
        </div>
      </div>
      {loading ? (
        <LoadingPage />
      ) : companies?.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="px-5 py-3 bg-gray-200 rounded-2xl text-gray-600">
            {t("There is no company")}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 2xl:grid-cols-4 gap-4 overflow-y-scroll transition duration-500 ease-in-out">
          {companies.map((company, index) => {
            return (
              <CompanyCard
                key={index}
                company={company}
                companyModulePermission={companyModulePermission}
                userModulePermission={userModulePermission}
              />
            );
          })}
        </div>
      )}

      <CreateUserModal errors={errors} />
      <CreateCompanyModal />
      <DuplicateCompanyModal />
      <BatchUploadModal />
      <DeleteConfirmModal
        handleDelete={() => handleDeleteCompany()}
        name={selectedCompanyName || ""}
        isDeleteConfirmModalOpen={isDeleteConfirmModalOpen}
        closeDeleteConfirmModal={closeDeleteConfirmModal}
      />
    </div>
  );
});

export default CompanyManagementPage;
